import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { RouterLink } from '@angular/router';

import { TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ButtonComponent } from '@personal-area/global/ui';
import {
  BasicVerificationStatus,
  IDVerificationStatus,
  SummaryVerificationFacade,
  VerificationNavigatorService,
} from '@personal-area/global/verification';
import { IconSpriteModule } from '@personal-area/icon-sprite';

import { DepositBonusTrackingService } from '../services/deposit-bonus-tracking.service';

export interface DepositBonusTermsDialogData {
  wasFirstBonusReceived: boolean;
  termsLink: string;
  fromDeposit: boolean;
}

@Component({
  templateUrl: './deposit-bonus-terms-dialog.component.html',
  styleUrls: ['./deposit-bonus-terms-dialog.component.scss'],
  standalone: true,
  imports: [
    MatDialogModule,
    TranslateModule,
    ButtonComponent,
    IconSpriteModule,
    RouterLink,
    NgTemplateOutlet,
    AsyncPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DepositBonusTermsDialogComponent implements OnInit {
  protected data: DepositBonusTermsDialogData = inject(MAT_DIALOG_DATA);

  protected isFullVerification$!: Observable<boolean>;
  protected basicVerification$!: Observable<BasicVerificationStatus>;

  constructor(
    private _summaryVerificationFacade: SummaryVerificationFacade,
    private _verificationNavigatorService: VerificationNavigatorService,
    private readonly _depositBonusTrackingService: DepositBonusTrackingService,
  ) {
    this.isFullVerification$ = this._summaryVerificationFacade.summary$.pipe(
      map((summary) => summary.idStatus === IDVerificationStatus.accepted),
    );

    this.basicVerification$ = this._summaryVerificationFacade.summary$.pipe(
      map((summary) => summary.basicStatus),
    );
  }

  ngOnInit(): void {
    if (this.data.wasFirstBonusReceived) {
      this._depositBonusTrackingService.depositBonusDialogViewed();
    }
  }

  protected onButtonClicked(isFullVerification: boolean): void {
    if (isFullVerification && !this.data.fromDeposit) {
      this._depositBonusTrackingService.depositFromBonusTapped();

      return;
    }

    if (!isFullVerification) {
      this._depositBonusTrackingService.getVerifiedTapped();
    }
  }

  protected getBtnInfo(
    isFullVerification: boolean | null,
    basicVerification: BasicVerificationStatus | null,
  ): {
    title: string;
    routerLink: string[] | undefined;
  } {
    if (!isFullVerification) {
      return {
        title: 'finances.btn.get-verified',
        routerLink:
          this._verificationNavigatorService.getVerificationUrlByStatus(
            basicVerification,
          ),
      };
    }

    if (this.data.fromDeposit) {
      // Back to deposit
      return {
        title: 'deposit-bonus.terms.button',
        routerLink: undefined,
      };
    }

    // Go to deposit
    return {
      title: 'finances.deposit',
      routerLink: ['/finances/deposit'],
    };
  }
}
